<script setup lang="ts">
import { richTextOrNull } from '@/composables/helpers/html';
const gradientCirclesSvg = '/img/2026/gradient-circles.svg';
const shardPng = '/img/2026/shard-hero-bottom-left.png';
const defaultVideoWebm = '/img/2026/hero-video/Inbound26Tease_120125-sm.webm';
const defaultVideoMp4 = '/img/2026/hero-video/Inbound26Tease_120125-sm.mp4';
const defaultVideoPoster = '/img/2026/hero-video/Inbound26Tease_120125-sm-poster.jpg';

interface HeroButton {
    label?: string;
    title?: string;
    url?: string;
}

interface HeroVideo {
    video?: string;
    webm?: string;
    poster?: string;
    altText?: string;
}

interface HeroDates {
    start?: string;
    end?: string;
}

const props = withDefaults(defineProps<{
    headline?: string;
    copy?: string;
    bodyText?: string;
    dates?: HeroDates;
    location?: string;
    venue?: string;
    button?: HeroButton;
    video?: HeroVideo;
    youtubeID?: string;
}>(), {
    headline: 'Boundless thinking starts at UNBOUND.',
});

import { stripBlockTags } from '@/composables/helpers/html';

const sanitizedHeadline = computed(() => stripBlockTags(props.headline));

const hasCustomVideo = computed(() => !!props.video?.video);
const hasVideo = computed(() => !props.video || !!props.video.video);
const heroVideoWebm = computed(() => hasCustomVideo.value ? props.video?.webm : defaultVideoWebm);
const heroVideoMp4 = computed(() => props.video?.video || defaultVideoMp4);
const heroVideoPoster = computed(() => props.video?.poster || defaultVideoPoster);
const ctaLabel = computed(() => props.button?.label || 'Register now');
const ctaUrl = computed(() => props.button?.url || '/register');
const ctaTitle = computed(() => props.button?.title || ctaLabel.value);

const subHead = computed(() => {
    if (props.dates?.start) {
        const start = new Date(props.dates.start);
        const end = props.dates?.end ? new Date(props.dates.end) : null;
        const month = start.toLocaleString('en-US', { month: 'long' });
        const startDay = start.getDate();
        const endDay = end?.getDate();
        const year = start.getFullYear();
        const dateStr = endDay ? `${month} ${startDay} - ${endDay}, ${year}` : `${month} ${startDay}, ${year}`;
        const loc = props.location ? ` in ${props.location}` : '';
        return `${dateStr}${loc}`;
    }
    return props.location || '';
});

// Preload hero video poster (LCP element) so browser discovers it during HTML parse
useHead({
    link: [{ rel: 'preload', as: 'image', href: heroVideoPoster.value, fetchpriority: 'high' }],
});

const posterReady = ref(false);
const videoStarted = ref(false);
const videoPlaying = ref(false);
const desktopVideo = ref(null);
const mobileVideo = ref(null);
const sectionRef = ref(null);
let resumeTimer = null;

const playVideos = () => {
    const dp = desktopVideo.value?.play();
    const mp = mobileVideo.value?.play();
    Promise.all([dp, mp].filter(Boolean))
        .then(() => { videoPlaying.value = true; })
        .catch(() => { videoPlaying.value = false; });
};

const toggleVideo = () => {
    const desktop = desktopVideo.value;
    const mobile = mobileVideo.value;
    const playing = desktop ? !desktop.paused : mobile ? !mobile.paused : false;
    if (playing) {
        desktop?.pause();
        mobile?.pause();
        videoPlaying.value = false;
    } else {
        playVideos();
    }
};

onMounted(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Wait for poster image to load before triggering mask animation
    const img = new Image();
    img.onload = () => {
        posterReady.value = true;
        if (!hasVideo.value) return;
        // Skip animation delay when user prefers reduced motion
        const delay = prefersReducedMotion ? 0 : 2500;
        setTimeout(() => {
            videoStarted.value = true;
            playVideos();
        }, delay);
    };
    img.src = heroVideoPoster.value;

    // Pause video when off-screen, resume 1s after re-entering
    const visibilityObs = new IntersectionObserver(([entry]) => {
        if (!entry.isIntersecting) {
            clearTimeout(resumeTimer);
            desktopVideo.value?.pause();
            mobileVideo.value?.pause();
            videoPlaying.value = false;
        } else if (videoStarted.value) {
            resumeTimer = setTimeout(() => {
                playVideos();
            }, 1000);
        }
    });
    if (sectionRef.value) visibilityObs.observe(sectionRef.value);
});

onUnmounted(() => {
    clearTimeout(resumeTimer);
});
</script>

<template>
    <section ref="sectionRef" class="block-2026-hero relative overflow-hidden isolate text-white
        flex flex-col gap-6 items-start
        min-h-[400px] pl-6 pr-(--box-padding-lr-m) pt-24 pb-0
        md:gap-[30px] md:min-h-[711px] md:pt-[152px] md:pb-[192px] md:pl-[40px] md:pr-0"
        :style="{ '--shard-bg': `url(${shardPng})` }">

        <!-- Mobile video: below text/CTA, masked + orange glow -->
        <div class="relative z-1 w-full order-3 mt-4 -mr-(--box-padding-lr-m)
            ml-auto aspect-406/293 md:hidden">
            <!-- Blurred orange glow (overflows the container) -->
            <div class="absolute left-[20%] top-[20%] w-[130px] h-[130px] pointer-events-none">
                <div class="absolute inset-[-66%_-67%] rounded-full bg-[var(--orange,#FF4800)] blur-[100px]" />
            </div>
            <!-- Masked video -->
            <div class="hero-mobile-video absolute inset-0 overflow-hidden" aria-hidden="true">
                <video v-if="hasVideo" ref="mobileVideo" autoplay muted loop playsinline
                    :poster="heroVideoPoster" fetchpriority="high" :alt="video?.altText"
                    class="w-full h-full object-cover">
                    <source v-if="heroVideoWebm" :src="heroVideoWebm" type="video/webm" />
                    <source :src="heroVideoMp4" type="video/mp4" />
                </video>
                <img v-else :src="heroVideoPoster" :alt="video?.altText"
                    class="w-full h-full object-cover" />
                <!-- Pink gradient overlay (matches desktop) -->
                <div class="absolute inset-0"
                    style="background: linear-gradient(to bottom, rgba(253, 46, 203, 0) 0%, rgba(253, 46, 203, 0.5) 100%);" />
            </div>
            <!-- Mobile video pause/play control -->
            <button v-if="videoStarted"
                class="group hero-pause-btn flex md:hidden absolute bottom-[10px] right-[10px] z-[4]
                    w-[31px] h-[31px] rounded-full
                    bg-[var(--magenta)] hover:bg-white border-none cursor-pointer
                    items-center justify-center
                    transition-colors duration-200
                    focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
                :aria-label="videoPlaying ? 'Pause video' : 'Play video'"
                @click="toggleVideo">
                <svg v-if="videoPlaying" aria-hidden="true" width="8" height="9" viewBox="0 0 8 9" fill="none">
                    <rect width="2.5" height="9" rx="0.5" class="fill-white group-hover:fill-(--magenta) transition-[fill] duration-200" />
                    <rect x="5.5" width="2.5" height="9" rx="0.5" class="fill-white group-hover:fill-(--magenta) transition-[fill] duration-200" />
                </svg>
                <svg v-else aria-hidden="true" width="8" height="9" viewBox="0 0 8 9" fill="none">
                    <path d="M8 4.5L0 9V0L8 4.5Z" class="fill-white group-hover:fill-(--magenta) transition-[fill] duration-200" />
                </svg>
            </button>
        </div>

        <!-- Hero Text -->
        <div class="hero-slide-in relative z-[3] flex flex-col gap-3 w-full order-1
            md:gap-3 md:pr-[58%] lg:pr-[55%] md:order-0">
            <h1 class="font-hubspot-serif text-white normal-case
                text-[calc(46/var(--base-fs)*1rem)] leading-[1.15] tracking-normal mb-0
                md:text-[calc(72/var(--base-fs)*1rem)] md:leading-[1.1]"
                v-html="sanitizedHeadline" />
            <p v-if="subHead" class="font-hubspot-sans font-light normal-case
                text-[calc(20/var(--base-fs)*1rem)] leading-[1.5] tracking-[0.4px]
                md:text-[calc(24/var(--base-fs)*1rem)] md:tracking-[0.48px]">
                {{ subHead }}
            </p>
            <div v-if="richTextOrNull(copy)" class="font-hubspot-sans font-light normal-case
                text-[calc(18/var(--base-fs)*1rem)] leading-[1.5] tracking-[0.36px]
                md:text-[calc(24/var(--base-fs)*1rem)] md:tracking-[0.48px]"
                v-html="copy" />
            <div v-if="richTextOrNull(bodyText)" class="font-hubspot-sans font-light normal-case
                text-[16px] leading-[26px] tracking-[0.32px]"
                v-html="bodyText" />
        </div>

        <!-- CTA Button -->
        <MessLink v-if="button?.label" :href="ctaUrl" :label="ctaLabel"
            v-posthog-capture="{ name: 'cta_clicked', properties: { cta_type: 'hero', button_label: ctaLabel, button_url: ctaUrl } }"
            class="hero-slide-in relative z-[2] font-hubspot-serif inline-flex items-center gap-[10px]
            px-5 py-2.5 order-2
            bg-white text-(--magenta) no-underline border-none
            hover:bg-(--orange) hover:text-white
            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white
            focus-visible:ring-offset-2 focus-visible:ring-offset-(--magenta)
            rounded-[50px]
            text-[calc(16/var(--base-fs)*1rem)] leading-[1.3] tracking-[0.32px]
            md:text-[calc(17/var(--base-fs)*1rem)] md:leading-[1.4] md:tracking-[0.34px] md:order-0
            transition-colors duration-200 ease-in-out">
            {{ ctaLabel }}
        </MessLink>

        <!-- Background (decorative) -->
        <div aria-hidden="true" class="absolute inset-0 z-[1] pointer-events-none">

            <!-- Gradient circles -->
            <div class="hero-gradient-circles hidden md:display-block absolute inset-0 overflow-visible">
                <img :src="gradientCirclesSvg" alt="" width="1440" height="711"
                    class="absolute display-block
                        top-[-60%] left-0 w-full h-[270%]
                        md:top-[-60%] md:left-0 md:w-[117.71%] md:h-[270%]" />
            </div>

            <!-- Masked video: organic SVG shape, mask rotates in to reveal -->
            <div class="hidden md:display-block absolute top-0 right-0 w-[49.31%] h-full">
                <!-- Mask wrapper: rotates in, carrying the mask shape -->
                <div :class="['absolute inset-0 opacity-0', posterReady && 'hero-video-masked']">
                    <!-- Content: counter-rotates so image stays visually static -->
                    <div :class="['absolute inset-[-10%]', posterReady && 'hero-video-content']">
                        <video v-if="hasVideo" ref="desktopVideo" muted loop playsinline
                            :poster="heroVideoPoster" fetchpriority="high" :alt="video?.altText"
                            class="absolute inset-0 w-full h-full object-cover">
                            <source v-if="heroVideoWebm" :src="heroVideoWebm" type="video/webm" />
                            <source :src="heroVideoMp4" type="video/mp4" />
                        </video>
                        <img v-else :src="heroVideoPoster" :alt="video?.altText"
                            class="absolute inset-0 w-full h-full object-cover" />
                        <!-- Pink gradient overlay -->
                        <div class="absolute inset-0"
                            style="background: linear-gradient(to bottom, rgba(253, 46, 203, 0) 0%, rgba(253, 46, 203, 0.5) 100%);" />
                    </div>
                </div>
                <!-- Desktop video pause/play control -->
                <button v-if="videoStarted"
                    class="group hero-pause-btn hidden md:flex absolute md:bottom-55 lg:bottom-[16%] xl:bottom-27.5 2xl:bottom-[3%] right-5 z-4
                        w-[31px] h-[31px] rounded-full
                        bg-[var(--magenta)] hover:bg-white border-none cursor-pointer
                        items-center justify-center pointer-events-auto
                        transition-colors duration-200
                        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
                    :aria-label="videoPlaying ? 'Pause video' : 'Play video'"
                    @click="toggleVideo">
                    <svg v-if="videoPlaying" aria-hidden="true" width="8" height="9" viewBox="0 0 8 9" fill="none">
                        <rect width="2.5" height="9" rx="0.5" class="fill-white group-hover:fill-(--magenta) transition-[fill] duration-200" />
                        <rect x="5.5" width="2.5" height="9" rx="0.5" class="fill-white group-hover:fill-(--magenta) transition-[fill] duration-200" />
                    </svg>
                    <svg v-else aria-hidden="true" width="8" height="9" viewBox="0 0 8 9" fill="none">
                        <path d="M8 4.5L0 9V0L8 4.5Z" class="fill-white group-hover:fill-(--magenta) transition-[fill] duration-200" />
                    </svg>
                </button>
            </div>

            <!-- Shard: ::after pseudo-element in <style> -->
        </div>

    </section>
</template>

<style>
/* Superscript in headline */
.block-2026-hero h1 sup {
    font-family: var(--font-hubspot-sans);
    font-size: 0.2em;
    vertical-align: 2.4em;
    line-height: 0;
}

/* Radial gradient background */
.block-2026-hero {
    background: radial-gradient(
        ellipse at 60.83% 21.66%,
        #820ca7 0%,
        #640969 48.8%,
        #55084a 73.2%,
        #46062b 97.6%
    );
}

/* Pause/play button fades in */
.hero-pause-btn {
    animation: hero-fade-in 0.4s ease-out both;
}

@keyframes hero-fade-in {
    from { opacity: 0; }
    to { opacity: 1; }
}

/* Text and button slide in from left */
.hero-slide-in {
    animation: hero-slide-left 1.2s cubic-bezier(0.22, 1, 0.36, 1) 0.3s both;
}

.hero-slide-in + .hero-slide-in {
    animation-delay: 0.5s;
}

@keyframes hero-slide-left {
    from {
        transform: translateX(-40px);
        opacity: 0;
    }
    to {
        transform: translateX(0);
        opacity: 1;
    }
}

/* Gradient circles fade in from flat purple background */
.hero-gradient-circles {
    animation: hero-gradients-in 3s ease-out 0.5s both;
}

@keyframes hero-gradients-in {
    from { opacity: 0; }
    to { opacity: 1; }
}

/* Mobile video mask */
.hero-mobile-video {
    --mobile-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='406' height='293' viewBox='0 0 406 293' fill='none'%3E%3Cpath d='M12.3815 182.406L189.132 11.3997C198.282 2.60387 211.098-1.42913 224.042 0.454558L472.665 39.9324C488.925 42.5205 502.418 54.0477 507.547 69.8607L571.576 263.436C579.085 285.99 567.225 309.973 545.131 316.885L119.758 448.414C97.6638 455.326 73.5482 442.409 66.2411 419.724L2.21182 226.149C-2.91722 210.336 1.00961 193.502 12.5825 182.276L12.3815 182.406Z' fill='%23fff'/%3E%3C/svg%3E");
    -webkit-mask-image: var(--mobile-mask);
    mask-image: var(--mobile-mask);
    -webkit-mask-size: cover;
    mask-size: cover;
    -webkit-mask-position: center;
    mask-position: center;
    -webkit-mask-repeat: no-repeat;
    mask-repeat: no-repeat;
}

/* Desktop video mask: organic SVG shape, scales uniformly anchored to left */
.hero-video-masked,
.hero-video-content {
    will-change: transform;
}

.hero-video-masked {
    --video-mask: url("data:image/svg+xml,%3Csvg width='710' height='646' viewBox='0 0 710 646' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M2.91512 360.128C-8.83675 403.517 16.023 448.713 58.0586 460.464L710 645.052V0H97.4393L2.91512 360.128Z' fill='%23fff'/%3E%3C/svg%3E");
    -webkit-mask-image: var(--video-mask);
    mask-image: var(--video-mask);
    -webkit-mask-size: cover;
    mask-size: cover;
    -webkit-mask-position: left top;
    mask-position: left top;
    -webkit-mask-repeat: no-repeat;
    mask-repeat: no-repeat;
    transform-origin: bottom left;
    animation: hero-mask-in 2s cubic-bezier(0.22, 1, 0.36, 1) 0.5s both;
}

/* Content counter-rotates so the image stays still while the mask sweeps in */
.hero-video-content {
    transform-origin: bottom left;
    animation: hero-content-counter 2s cubic-bezier(0.22, 1, 0.36, 1) 0.5s both;
}

@keyframes hero-mask-in {
    from {
        transform: rotate(8deg) scale(1.3);
        opacity: 0;
    }
    to {
        transform: rotate(0deg) scale(1);
        opacity: 1;
    }
}

@keyframes hero-content-counter {
    from {
        transform: rotate(-8deg) scale(0.769);
    }
    to {
        transform: rotate(0deg) scale(1);
    }
}

/* Shard: flattened PNG pinned to bottom-left */
@media (min-width: 768px) {
    .block-2026-hero::after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        width: 50.07%;
        aspect-ratio: 721 / 396;
        background-image: var(--shard-bg);
        background-size: contain;
        background-repeat: no-repeat;
        pointer-events: none;
        z-index: 0;
        transform-origin: bottom right;
        animation: hero-shard-in 2s cubic-bezier(0.22, 1, 0.36, 1) both;
    }
}

@keyframes hero-shard-in {
    from {
        transform: rotate(-12deg);
        opacity: 0;
    }
    to {
        transform: rotate(0deg);
        opacity: 1;
    }
}

/* Respect reduced motion preferences */
@media (prefers-reduced-motion: reduce) {
    .hero-slide-in,
    .hero-gradient-circles,
    .hero-pause-btn,
    .hero-video-masked,
    .hero-video-content {
        animation: none !important;
        opacity: 1 !important;
        transform: none !important;
    }

    .block-2026-hero::after {
        animation: none !important;
        opacity: 1 !important;
        transform: none !important;
    }
}
</style>
